import { useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import {
  Boxes,
  LayoutGrid,
  LogOut,
  Menu,
  Receipt,
  RefreshCw,
  Settings,
  ShieldCheck,
  Tag,
  Truck,
  Users,
  UtensilsCrossed,
  Wallet,
  Wrench,
  X,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { HealthIndicator } from "@/components/HealthIndicator";
import { Logo, StatusPill } from "@/components/ui";
import { apiRequest } from "@/lib/api";
import { cn } from "@/lib/cn";
import { clearTenantLocalCache } from "@/lib/offline/db";
import { useOnlineStatus, useOutbox } from "@/lib/offline/hooks";
import { getSession, setSession, updateSessionTenant, type TenantPublic } from "@/lib/session";
import { applyTenantTheme, getEffectiveTenantSettings } from "@/lib/theme";

interface NavItem {
  label: string;
  icon: LucideIcon;
  to?:
    | "/pos"
    | "/login"
    | "/"
    | "/contacto"
    | "/caja"
    | "/compras"
    | "/inventario"
    | "/productos"
    | "/taller"
    | "/restaurante"
    | "/clientes"
    | "/reportes"
    | "/superadmin"
    | "/administracion"
    | "/brand";
  phase?: string;
  service?: "pos" | "restaurant" | "workshop";
  requiresSuperadmin?: boolean;
  requiresClientAdmin?: boolean;
}

/**
 * Menú de navegación modular adaptado a los servicios contratados por cada negocio.
 */
const NAV_ITEMS: readonly NavItem[] = [
  { label: "Venta", icon: LayoutGrid, to: "/pos", service: "pos" },
  { label: "Restaurante", icon: UtensilsCrossed, to: "/restaurante", service: "restaurant" },
  { label: "Taller", icon: Wrench, to: "/taller", service: "workshop" },
  { label: "Productos", icon: Tag, to: "/productos" },
  { label: "Inventario", icon: Boxes, to: "/inventario" },
  { label: "Caja", icon: Wallet, to: "/caja" },
  { label: "Compras", icon: Truck, to: "/compras" },
  { label: "Clientes", icon: Users, to: "/clientes" },
  { label: "Reportes", icon: Receipt, to: "/reportes" },
  { label: "Superadmin", icon: ShieldCheck, to: "/superadmin", requiresSuperadmin: true },
  { label: "Configuración", icon: Settings, to: "/brand" },
];

const PAGE_TITLES: Record<string, string> = {
  "/pos": "Punto de venta",
  "/": "Inicio",
  "/login": "Iniciar Sesión",
  "/contacto": "Contacto y Soporte",
  "/productos": "Catálogo de productos y servicios",
  "/caja": "Caja y turnos",
  "/compras": "Compras y cuentas por pagar",
  "/inventario": "Control de inventario y almacenes",
  "/taller": "Órdenes de servicio",
  "/taller/$orderId": "Orden de servicio",
  "/restaurante": "Restaurante y comandas",
  "/restaurante/comanda/$checkId": "Detalle de comanda",
  "/clientes": "Directorio de clientes",
  "/reportes": "Reportes y analítica de negocio",
  "/administracion": "Configuración del sistema",
  "/superadmin": "Panel Superadministrador SaaS",
  "/brand": "Configuración del sistema",
};

export function AppShell({ onLogout }: { onLogout: () => void }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const queryClient = useQueryClient();
  // La ruta de detalle lleva un folio en la URL, así que se busca por prefijo.
  const title =
    PAGE_TITLES[pathname] ??
    (pathname.startsWith("/taller/")
      ? "Orden de servicio"
      : pathname.startsWith("/restaurante/")
      ? "Comanda"
      : "MegaSyR");
  const session = getSession();
  const online = useOnlineStatus();
  const outbox = useOutbox();

  const [isImpersonating] = useState(() =>
    Boolean(localStorage.getItem("megasyr.impersonator"))
  );

  // Sincronizar configuración de apariencia y ticket del negocio activo desde el servidor
  useEffect(() => {
    const activeTenantId = session?.tenant?.id;
    if (!activeTenantId || !online) return;
    let cancelled = false;
    apiRequest<TenantPublic>("/api/v1/tenants/current")
      .then((freshTenant) => {
        if (cancelled) return;
        updateSessionTenant(freshTenant);
        applyTenantTheme(getEffectiveTenantSettings(freshTenant), freshTenant.slug);
      })
      .catch(() => {
        // Sin permisos o sin red: usar configuración en caché
      });
    return () => {
      cancelled = true;
    };
  }, [session?.tenant?.id, online]);

  async function exitImpersonation() {
    const raw = localStorage.getItem("megasyr.impersonator");
    if (raw) {
      try {
        const orig = JSON.parse(raw);
        localStorage.removeItem("megasyr.impersonator");
        await clearTenantLocalCache();
        queryClient.clear();
        setSession(orig);
        window.location.href = "/superadmin";
      } catch {
        localStorage.removeItem("megasyr.impersonator");
        window.location.href = "/";
      }
    }
  }

  const syncRef = useRef(outbox.sync);
  const pendingRef = useRef(outbox.pending);
  const syncingRef = useRef(outbox.syncing);
  syncRef.current = outbox.sync;
  pendingRef.current = outbox.pending;
  syncingRef.current = outbox.syncing;

  useEffect(() => {
    const intentar = () => {
      if (navigator.onLine && pendingRef.current > 0 && !syncingRef.current) {
        syncRef.current();
      }
    };

    intentar();
    const latido = setInterval(intentar, 5_000);
    window.addEventListener("online", intentar);
    return () => {
      clearInterval(latido);
      window.removeEventListener("online", intentar);
    };
  }, []);

  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <div className="bg-ink flex min-h-screen text-white">
      {/* Sidebar permanente en desktop */}
      <Sidebar className="hidden lg:flex" />

      {/* Drawer para móviles y tablets (< lg) */}
      {mobileNavOpen ? (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm lg:hidden animate-in fade-in duration-200"
            onClick={() => setMobileNavOpen(false)}
          />
          <aside className="fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-ink border-r border-white/10 flex flex-col shadow-2xl lg:hidden animate-in slide-in-from-left duration-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div className="flex items-center gap-2.5">
                <Logo variant="isotipo" tone="white" className="h-8" />
                <Logo variant="wordmark" tone="white" className="h-4.5" alt="" />
              </div>
              <button
                type="button"
                onClick={() => setMobileNavOpen(false)}
                aria-label="Cerrar menú de navegación"
                className="p-1.5 rounded-msr text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              >
                <X className="size-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-2">
              <SidebarContent onNavigate={() => setMobileNavOpen(false)} />
            </div>

            <div className="border-white/10 space-y-1 border-t px-5 py-4 text-xs">
              <p className="font-semibold text-white/80">{session?.branches[0]?.name ?? "Sin sucursal"}</p>
              <p className="text-white/45">Caja 1 · turno según pantalla de caja</p>
              <p className="pt-2 text-[10px] text-white/30">v0.6.0 · Fase 5</p>
            </div>
          </aside>
        </>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Banner de suplantación superadmin */}
        {isImpersonating ? (
          <div className="bg-amber-500/20 border-b border-amber-500/30 px-4 sm:px-6 py-2 text-xs font-medium text-amber-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="size-2 rounded-full bg-amber-400 animate-pulse shrink-0" />
              <span>
                <strong>Modo Soporte Superadmin:</strong> Operando como cliente en{" "}
                <strong>{session?.tenant.name}</strong> ({session?.tenant.slug}).
              </span>
            </div>
            <button
              type="button"
              onClick={() => void exitImpersonation()}
              className="rounded bg-amber-400 px-2.5 py-0.5 text-xs font-bold text-ink hover:bg-amber-300 transition-colors shrink-0"
            >
              Salir de suplantación
            </button>
          </div>
        ) : null}

        <header className="border-white/10 bg-ink flex h-16 shrink-0 items-center justify-between gap-3 sm:gap-4 border-b px-4 sm:px-6 text-white">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              aria-label="Abrir menú de navegación"
              className="lg:hidden -ml-1 p-2 rounded-msr text-white/70 hover:text-white hover:bg-white/10 transition-colors shrink-0"
            >
              <Menu className="size-5" />
            </button>
            <div className="min-w-0">
              <p className="text-[11px] font-medium tracking-widest text-white/55 uppercase truncate">
                {session?.tenant.name ?? "MegaSyR"} · {session?.branches[0]?.name ?? "Sin sucursal"}
              </p>
              <h1 className="font-display truncate text-base sm:text-lg font-bold text-white">{title}</h1>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {!online ? <StatusPill tone="warning">Sin conexión</StatusPill> : null}
            {outbox.pending > 0 ? (
              <button
                type="button"
                onClick={outbox.sync}
                disabled={!online || outbox.syncing}
                className="border-electric/40 text-white hover:bg-electric/20 flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold disabled:opacity-50"
              >
                <RefreshCw aria-hidden className={cn("size-3", outbox.syncing && "animate-spin")} />
                <span className="hidden sm:inline">{outbox.pending} por enviar</span>
                <span className="sm:hidden">{outbox.pending}</span>
              </button>
            ) : null}
            <HealthIndicator />
            <UserChip
              name={session?.user.full_name ?? "Sin sesión"}
              role={session?.roles[0] ?? "—"}
              onLogout={onLogout}
            />
          </div>
        </header>

        <main className="bg-surface text-carbon min-w-0 flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function Sidebar({ className }: { className?: string }) {
  const session = getSession();

  return (
    <aside className={cn("bg-ink border-white/10 w-64 shrink-0 flex-col border-r", className)}>
      <div className="flex items-center gap-3 px-5 py-5">
        <Logo variant="isotipo" tone="white" className="h-10" />
        <Logo variant="wordmark" tone="white" className="h-5" alt="" />
      </div>

      <SidebarContent />

      <div className="border-white/10 space-y-1 border-t px-5 py-4 text-xs">
        <p className="font-semibold text-white/80">{session?.branches[0]?.name ?? "Sin sucursal"}</p>
        <p className="text-white/45">Caja 1 · turno según pantalla de caja</p>
        <p className="pt-2 text-[10px] text-white/30">v0.6.0 · Fase 5</p>
      </div>
    </aside>
  );
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const session = getSession();
  const isAdminTenant =
    session?.tenant?.slug === "admin" || Boolean(session?.user?.is_superadmin);
  const tenant = session?.tenant;

  const visibleItems = NAV_ITEMS.filter((item) => {
    if (item.requiresSuperadmin) {
      return isAdminTenant;
    }
    if (item.requiresClientAdmin) {
      return !isAdminTenant;
    }
    if (item.service === "pos") {
      return isAdminTenant || tenant?.has_pos !== false;
    }
    if (item.service === "restaurant") {
      return isAdminTenant || Boolean(tenant?.has_restaurant);
    }
    if (item.service === "workshop") {
      return isAdminTenant || Boolean(tenant?.has_workshop) || tenant?.slug === "taller";
    }
    return true;
  });

  return (
    <nav className="flex-1 space-y-1 px-3">
      {visibleItems.map((item) => {
        const Icon = item.icon;
        const base =
          "flex items-center gap-3 rounded-msr px-3 py-2.5 text-sm font-medium transition-colors";

        if (!item.to) {
          return (
            <span
              key={item.label}
              aria-disabled="true"
              className={cn(base, "cursor-not-allowed text-white/35")}
            >
              <Icon aria-hidden className="size-4.5 shrink-0" />
              <span className="flex-1">{item.label}</span>
              <span className="rounded-full border border-white/10 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide">
                {item.phase}
              </span>
            </span>
          );
        }

        return (
          <Link
            key={item.label}
            to={item.to}
            onClick={onNavigate}
            className={cn(base, "text-white/70 hover:bg-white/5 hover:text-white")}
            activeProps={{ className: "bg-white/10 text-white" }}
            activeOptions={{ exact: item.to === "/" }}
          >
            <Icon aria-hidden className="size-4.5 shrink-0" />
            <span className="flex-1">{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

function UserChip({
  name,
  role,
  onLogout,
}: {
  name: string;
  role: string;
  onLogout: () => void;
}) {
  const initials = name
    .split(" ")
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

  return (
    <div className="flex items-center gap-2.5">
      <div className="text-white grid size-9 place-items-center rounded-full bg-white/10 text-xs font-bold">
        {initials || "??"}
      </div>
      <div className="hidden leading-tight sm:block">
        <p className="text-sm font-semibold">{name}</p>
        <p className="text-[11px] text-white/45 capitalize">{role.replace("_", " ")}</p>
      </div>
      <button
        type="button"
        onClick={onLogout}
        aria-label="Cerrar sesión"
        title="Cerrar sesión"
        className="grid size-9 place-items-center rounded-full text-white/50 transition-colors hover:bg-white/10 hover:text-white"
      >
        <LogOut aria-hidden className="size-4" />
      </button>
    </div>
  );
}
